/**
 * Subscription Management Modal
 * Update company subscription limits and validity
 */
import React, { useEffect } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Select,
  Button,
  message,
  Space,
  Alert,
  Descriptions,
  Switch,
} from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import superAdminService, { Company, SubscriptionUpdate } from '../../services/superAdminService';
import '../../styles/modalStyles.css';

const { Option } = Select;

interface SubscriptionModalProps {
  visible: boolean;
  company: Company | null;
  sidebarCollapsed?: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  visible,
  company,
  sidebarCollapsed = false,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);

  useEffect(() => {
    if (visible && company) {
      form.setFieldsValue({
        max_users: company.max_users,
        max_locations: company.max_locations,
        plan_type: company.plan_type,
      });
    }
  }, [visible, company, form]);

  const handleSubmit = async (values: any) => {
    if (!company) return;

    setLoading(true);
    try {
      const updateData: SubscriptionUpdate = {
        max_users: values.max_users,
        max_locations: values.max_locations,
        plan_type: values.plan_type || company.plan_type,
        extend_subscription: values.extend_subscription || false,
      };
      const period = values.subscription_period?.trim?.();
      if (period && (period === 'monthly' || period === 'yearly')) {
        updateData.subscription_period = period;
      }

      const response = await superAdminService.updateCompanySubscription(
        company.id,
        updateData
      );

      message.success('Subscription updated successfully!');

      if (response.subscription_status) {
        const status = response.subscription_status;
        if (status.is_expired) {
          message.warning('Company subscription has expired');
        } else if (status.days_remaining != null && status.days_remaining < 30) {
          message.warning(`Subscription expires in ${status.days_remaining} days`);
        }
      }

      onSuccess();
    } catch (error: any) {
      const msg =
        error?.response?.data?.detail ||
        (typeof error?.message === 'string' ? error.message : 'Failed to update subscription');
      message.error(Array.isArray(msg) ? msg.join(' ') : msg);
      console.error('Error updating subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!company) return null;

  const subscriptionStatus = company.subscription_expires_at
    ? (() => {
        const expiryDate = new Date(company.subscription_expires_at);
        const now = new Date();
        const daysRemaining = Math.ceil(
          (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
        );
        return {
          isExpired: daysRemaining < 0,
          daysRemaining: Math.max(0, daysRemaining),
          expiresAt: expiryDate,
        };
      })()
    : null;

  return (
    <Modal
      title={
        <Space>
          <CalendarOutlined />
          <span>Manage Subscription - {company.name}</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      getContainer={() => document.querySelector('.main-content-full') || document.body}
      wrapClassName="form-panel-full-wrap"
      width="100%"
      style={{ maxWidth: 'none' }}
      destroyOnClose
      zIndex={2000}
      footer={null}
      bodyStyle={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
      }}
    >
      {subscriptionStatus && (
        <Alert
          message={
            subscriptionStatus.isExpired
              ? 'Subscription Expired'
              : `Subscription Active - ${subscriptionStatus.daysRemaining} days remaining`
          }
          description={`Expires on: ${subscriptionStatus.expiresAt.toLocaleDateString()}`}
          type={subscriptionStatus.isExpired ? 'error' : subscriptionStatus.daysRemaining < 30 ? 'warning' : 'info'}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Descriptions title="Current Subscription" bordered column={1} size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Plan Type">
          {company.plan_type?.toUpperCase() || 'BASIC'}
        </Descriptions.Item>
        <Descriptions.Item label="Max Users">
          {company.max_users}
        </Descriptions.Item>
        <Descriptions.Item label="Max Locations">
          {company.max_locations}
        </Descriptions.Item>
        <Descriptions.Item label="Expires At">
          {company.subscription_expires_at
            ? new Date(company.subscription_expires_at).toLocaleDateString()
            : 'No expiration'}
        </Descriptions.Item>
      </Descriptions>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          max_users: company.max_users,
          max_locations: company.max_locations,
          plan_type: company.plan_type,
        }}
      >
        <Form.Item
          label="Plan Type"
          name="plan_type"
        >
          <Select
            onChange={(v) => {
              const plan = (v || 'basic').toString().toLowerCase();
              const presets: Record<string, { max_users: number; max_locations: number }> = {
                basic: { max_users: 2, max_locations: 1 }, // shown as Standard
                standard: { max_users: 2, max_locations: 1 },
                premium: { max_users: 6, max_locations: 4 },
                enterprise: { max_users: 15, max_locations: 6 },
              };
              const p = presets[plan] || presets.basic;
              form.setFieldsValue({ max_users: p.max_users, max_locations: p.max_locations });
            }}
          >
            <Option value="basic">Standard</Option>
            <Option value="premium">Premium</Option>
            <Option value="enterprise">Enterprise</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Max Users"
          name="max_users"
          rules={[{ required: true, message: 'Please enter max users' }]}
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label="Max Locations"
          name="max_locations"
          rules={[{ required: true, message: 'Please enter max locations' }]}
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label="Subscription Period"
          name="subscription_period"
          tooltip="Select to set or extend subscription validity"
        >
          <Select placeholder="Select subscription period">
            <Option value="monthly">Monthly (30 days)</Option>
            <Option value="yearly">Yearly (365 days)</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="extend_subscription"
          valuePropName="checked"
          tooltip="If checked, extends from current expiration date. If unchecked, starts from now."
        >
          <Switch
            checkedChildren="Extend from current"
            unCheckedChildren="Start from now"
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Update Subscription
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SubscriptionModal;
